import { z } from 'zod';

// Dynamic pricing (XT-951): the structure a product's pricing FUNCTION reads.
// Stored on `products.product.dynamic_pricing` (jsonb) and used only while
// `product.is_dynamic_pricing` is true — the static `price` stays on the row
// untouched. `priceDynamicProduct` (`@travel-planner/shared/pricing`) turns it
// plus the booking parameters into the same static price JSON an item carries
// today. Design: apps/products/prd/dynamic-pricing.md.

export const DYNAMIC_PRICING_VERSION = 1;

export const dynamicPricingOptionSchema = z.enum([
  'Night',
  'Pax',
  'Group',
  'Hour',
  'Day',
  'Unit',
  'Piece',
  'Vehicle',
]);
export type DynamicPricingOption = z.infer<typeof dynamicPricingOptionSchema>;

export const dynamicRateStatusSchema = z.enum([
  'static',
  'dynamic',
  'on_request',
  'from',
]);
export type DynamicRateStatus = z.infer<typeof dynamicRateStatusSchema>;

export const dynamicMarketSegmentSchema = z.enum(['FIT', 'Group']);

const isoDate = z.iso.date();
const optionalIsoDate = isoDate.nullable().optional();
const money = z.number().nonnegative();
const optionalCount = z.number().int().min(0).nullable().optional();
const optionalText = z.string().nullable().optional();
const id = z.string().min(1);

/** 0 = Monday … 6 = Sunday. An empty list means every day. */
const weekdaysSchema = z.array(z.number().int().min(0).max(6));

export const dynamicSeasonSchema = z.object({
  id,
  name: z.string(),
  from: isoDate,
  to: isoDate,
  /** Minimum nights a stay starting in this season must book. */
  minStay: optionalCount,
});
export type DynamicSeason = z.infer<typeof dynamicSeasonSchema>;

export const dynamicPriceRowSchema = z.object({
  id,
  seasonId: z.string().min(1),
  // Identity — which fields matter depends on the category and option.
  roomTypeId: optionalText,
  occupancy: optionalText,
  board: optionalText,
  /** Night option: the row applies to stays of at least this many nights. */
  minNights: z.number().int().min(1).nullable().optional(),
  travellerTypeId: z.uuid().nullable().optional(),
  ageFrom: optionalCount,
  ageTo: optionalCount,
  /** Group option: travellers the base amount covers. */
  basePax: z.number().int().min(1).nullable().optional(),
  paxMin: optionalCount,
  paxMax: optionalCount,
  minSpend: money.nullable().optional(),
  meal: optionalText,
  vehicle: optionalText,
  service: optionalText,
  maxPax: optionalCount,
  /** Minimum billable quantity (hours / days / units). */
  minQuantity: z.number().int().min(1).nullable().optional(),
  description: optionalText,
  // Money. `amount` is the supplier GROSS price (gross products) or the NET
  // cost (net products), both incl. cost VAT — same as the static `cost`.
  amount: money,
  /** Group option: price per traveller above `basePax`, same basis. */
  additionalPaxAmount: money.nullable().optional(),
  costVatId: z.uuid().nullable().optional(),
  priceVatId: z.uuid().nullable().optional(),
  /** Net products only. */
  markup: z.number().nullable().optional(),
});
export type DynamicPriceRow = z.infer<typeof dynamicPriceRowSchema>;

export const dynamicRoomTypeSchema = z.object({
  id,
  name: z.string(),
  code: optionalText,
  bedding: optionalText,
  maxPax: optionalCount,
  maxPaxExtraBed: optionalCount,
  sizeMin: z.number().nonnegative().nullable().optional(),
  sizeMax: z.number().nonnegative().nullable().optional(),
});
export type DynamicRoomType = z.infer<typeof dynamicRoomTypeSchema>;

/** Rooms under this size must be flagged to the agent before quoting. */
export const DYNAMIC_MIN_ROOM_SIZE_M2 = 25;

export const dynamicOperationSchema = z.object({
  /** 0 = Monday … 6 = Sunday. */
  days: weekdaysSchema,
  startTime: optionalText,
  endTime: optionalText,
  durationHours: z.number().nonnegative().nullable().optional(),
  pickup: optionalText,
  minAge: optionalCount,
  capacity: optionalCount,
  dressCode: optionalText,
  nightSupplementFrom: optionalText,
  /** End of the night window (blank: 06:00); earlier than its start = past midnight. */
  nightSupplementTo: optionalText,
  notes: optionalText,
});
export type DynamicOperation = z.infer<typeof dynamicOperationSchema>;

export const dynamicSurchargeTypeSchema = z.enum([
  'Weekday',
  'Event',
  'Holiday',
  'Festive',
  'Night supplement',
  'Overtime',
  'Blackout',
]);
export type DynamicSurchargeType = z.infer<typeof dynamicSurchargeTypeSchema>;

export const dynamicSurchargeBasisSchema = z.enum([
  'per_room_night',
  'per_pax',
  'per_booking',
  'per_hour',
  'per_vehicle',
]);
export type DynamicSurchargeBasis = z.infer<typeof dynamicSurchargeBasisSchema>;

export const dynamicAvailabilitySchema = z.enum([
  'open',
  'on_request',
  'stop_sell',
]);
export type DynamicAvailability = z.infer<typeof dynamicAvailabilitySchema>;

export const dynamicSurchargeSchema = z.object({
  id,
  type: dynamicSurchargeTypeSchema,
  name: z.string(),
  /** Blank dates = the whole contract. */
  from: optionalIsoDate,
  to: optionalIsoDate,
  weekdays: weekdaysSchema,
  /** Free-text day/hour rule shown to consultants ("After contracted hours"). */
  daysNote: optionalText,
  amount: money,
  unit: z.enum(['fixed', 'percent']),
  basis: dynamicSurchargeBasisSchema,
  availability: dynamicAvailabilitySchema,
});
export type DynamicSurcharge = z.infer<typeof dynamicSurchargeSchema>;

export const dynamicSupplementSchema = z.object({
  id,
  name: z.string(),
  pricingOption: dynamicPricingOptionSchema,
  travellerTypeId: z.uuid().nullable().optional(),
  /** Incl. VAT, same basis as the price rows. Null = on request. */
  amount: money.nullable().optional(),
  minQuantity: z.number().int().min(1),
  /** Mandatory supplements are folded into the item price. */
  mandatory: z.boolean(),
});
export type DynamicSupplement = z.infer<typeof dynamicSupplementSchema>;

export const dynamicPromotionTypeSchema = z.enum([
  'stay_pay',
  'percent_discount',
  'free_upgrade',
  'free_breakfast',
  'fb_credit',
  'gift',
]);
export type DynamicPromotionType = z.infer<typeof dynamicPromotionTypeSchema>;

export const dynamicPromotionSchema = z.object({
  id,
  type: dynamicPromotionTypeSchema,
  /** stay_pay: stay X nights, pay Y. */
  stayNights: z.number().int().min(1).nullable().optional(),
  payNights: z.number().int().min(0).nullable().optional(),
  /** percent_discount. */
  percent: z.number().min(0).max(100).nullable().optional(),
  bookBefore: optionalIsoDate,
  stayFrom: optionalIsoDate,
  stayTo: optionalIsoDate,
  minNights: z.number().int().min(1).nullable().optional(),
  /** Empty = every room type. */
  roomTypeIds: z.array(z.string()),
  /** Arrival weekdays that do not qualify (0 = Monday). */
  excludedArrivalWeekdays: weekdaysSchema,
  combinable: z.boolean(),
  usageLimit: optionalText,
  reference: optionalText,
  description: optionalText,
});
export type DynamicPromotion = z.infer<typeof dynamicPromotionSchema>;

export const dynamicCancellationTierSchema = z.object({
  id,
  daysBefore: z.number().int().min(0),
  fee: z.string(),
  appliesTo: z.string(),
});

export const dynamicTermsSchema = z.object({
  cancellationTiers: z.array(dynamicCancellationTierSchema),
  depositPercent: z.number().min(0).max(100).nullable().optional(),
  balanceDueDays: optionalCount,
  festiveDepositPercent: z.number().min(0).max(100).nullable().optional(),
  cardSurchargePercent: z.number().min(0).max(100).nullable().optional(),
  distributionRestrictions: optionalText,
});
export type DynamicTerms = z.infer<typeof dynamicTermsSchema>;

export const dynamicPricingSchema = z.object({
  version: z.literal(DYNAMIC_PRICING_VERSION),
  pricingOption: dynamicPricingOptionSchema,
  priceType: z.enum(['gross', 'net']),
  currency: z.string().min(1),
  supplierReference: optionalText,
  rateStatus: dynamicRateStatusSchema,
  /** Gross products: the supplier commission %. */
  commissionPercent: z.number().min(0).max(100).nullable().optional(),
  contractFrom: optionalIsoDate,
  contractTo: optionalIsoDate,
  marketSegment: dynamicMarketSegmentSchema.nullable().optional(),
  /** Head count (rooms for accommodation) from which group terms apply. */
  groupThreshold: optionalCount,
  /**
   * Used only when the product has no static price: what a night (Night), a
   * traveller (Pax) or the booking (other options) costs when no season or
   * price row covers the dates. Same basis as a price row; VAT and markup
   * come from the first price row. Required to switch on without a static
   * price.
   */
  fallbackAmount: money.nullable().optional(),
  seasons: z.array(dynamicSeasonSchema),
  rows: z.array(dynamicPriceRowSchema),
  roomTypes: z.array(dynamicRoomTypeSchema),
  operation: dynamicOperationSchema.nullable().optional(),
  surcharges: z.array(dynamicSurchargeSchema),
  supplements: z.array(dynamicSupplementSchema),
  promotions: z.array(dynamicPromotionSchema),
  terms: dynamicTermsSchema,
});
export type DynamicPricing = z.infer<typeof dynamicPricingSchema>;

// What an itinerary item records about its dynamic price
// (`itinerary_item.dynamic_pricing`). `auto` items are re-priced when a
// parameter changes; a consultant's own price edit flips them to `manual`,
// which stops it. `fingerprint` is the money the function last wrote — a write
// whose money differs from it is a hand edit.
export const itemDynamicPricingStatusSchema = z.enum(['auto', 'manual']);
export type ItemDynamicPricingStatus = z.infer<
  typeof itemDynamicPricingStatusSchema
>;

export const dynamicPricingWarningCodeSchema = z.enum([
  'NO_SEASON',
  'NO_RATE',
  'OUTSIDE_CONTRACT',
  'MIN_STAY',
  'STOP_SELL',
  'ON_REQUEST',
  'ABOVE_MAX_PAX',
  'GROUP_THRESHOLD',
  'ROOM_UNDER_MIN_SIZE',
  'SUPPLEMENT_ON_REQUEST',
  'NO_TRAVELLER_RATE',
  'NO_TRAVELLERS',
  'SPREAD_PER_BOOKING',
  'NO_DATES',
  'AGE_RATES_AVERAGED',
  'FALLBACK_PRICE',
  'MIXED_VAT',
  'NEGATIVE_PRICE',
  // Written by the itinerary, not the engine: an approved item re-priced.
  'PRICE_CHANGED_AFTER_APPROVAL',
]);
export type DynamicPricingWarningCode = z.infer<
  typeof dynamicPricingWarningCodeSchema
>;

export const dynamicPricingWarningSchema = z.object({
  code: dynamicPricingWarningCodeSchema,
  message: z.string(),
  /** The season it is about, when it is about one. */
  seasonId: z.string().optional(),
  /** The traveller type it is about, when it is about one. */
  travellerTypeId: z.string().optional(),
});
export type DynamicPricingWarning = z.infer<typeof dynamicPricingWarningSchema>;

export const itemDynamicPricingStateSchema = z.object({
  status: itemDynamicPricingStatusSchema,
  fingerprint: z.string(),
  pricedAt: z.string(),
  /** First quote (`YYYY-MM-DD`): re-pricing keeps book-before promotions. */
  quotedOn: z.iso.date(),
  /**
   * The organisation's default markup for the product's category when the
   * item was first quoted (`null`: none). Re-pricing keeps it, so a later
   * change of the setting reaches new items only. Absent on items priced
   * before it was recorded: their next pricing takes the setting of the day.
   */
  categoryMarkupPercent: z.number().nullable().optional(),
  warnings: z.array(dynamicPricingWarningSchema),
  appliedPromotions: z.array(z.string()),
  /**
   * The booking it was priced for (dates, travellers and their recorded
   * ages, rooms): a re-price runs only once these change. Absent on items
   * priced before it was recorded.
   */
  inputs: z.string().optional(),
  /**
   * Why a `manual` item stopped following the function when no one edited
   * its price: its product was taken off dynamic pricing. Re-apply clears it.
   */
  reason: z.enum(['productSwitchedOff']).optional(),
});
export type ItemDynamicPricingState = z.infer<
  typeof itemDynamicPricingStateSchema
>;

// itineraryItems.dynamicPricing / reapplyDynamicPricing.
export const itemDynamicPricingInput = z.object({ id: z.uuid() });

/**
 * Why an item following the function is no longer re-priced on its own: its
 * itinerary is sold, or the item is booked with the supplier.
 */
export const dynamicPricingFreezeSchema = z.enum(['sold', 'booked']);
export type DynamicPricingFreeze = z.infer<typeof dynamicPricingFreezeSchema>;

export const itemDynamicPricingResultSchema = z.object({
  /** Null when the item was never priced by a pricing function. */
  state: itemDynamicPricingStateSchema.nullable(),
  /** Whether the item's product is on dynamic pricing now. */
  productIsDynamic: z.boolean(),
  /** The item's product was deleted: nothing re-prices it, Re-apply included. */
  productDeleted: z.boolean(),
  /** Set while nothing re-prices the item automatically (Re-apply still does). */
  frozenBy: dynamicPricingFreezeSchema.nullable(),
  rateStatus: dynamicRateStatusSchema.nullable(),
  supplierReference: z.string().nullable(),
  terms: dynamicTermsSchema.nullable(),
  operation: dynamicOperationSchema.nullable(),
});
export type ItemDynamicPricingResult = z.infer<
  typeof itemDynamicPricingResultSchema
>;
